import { useEffect, useRef, useState, type ReactNode } from 'react';
import { S, t } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import styles from './matrix.module.css';
import { complete, runCommand, type ShellAction, type ShellLine } from './shell';

/** Nombre de lignes gardées à l'écran : au-delà, les plus vieilles tombent. */
const SCROLLBACK = 60;

interface Printed extends ShellLine {
  readonly id: number;
}

/**
 * Le terminal du skin matrix : l'invite du livre d'or, qui obéit aussi aux
 * commandes.
 *
 * Ce composant ne décide de rien — `shell.ts` lit la ligne et rend des lignes à
 * afficher plus, éventuellement, une action. Ici on ne fait que peindre la
 * sortie et brancher cette action sur le vrai site : le même moteur audio, le
 * même mixeur, le même compteur que partout ailleurs.
 */
export function MatrixShell(): ReactNode {
  const { lang, draft, seen, clock, playing } = useSteve();
  const actions = useActions();

  const [lines, setLines] = useState<readonly Printed[]>([]);
  const [history, setHistory] = useState<readonly string[]>([]);
  /** Position dans l'historique : `null` quand on écrit une ligne neuve. */
  const [recalled, setRecalled] = useState<number | null>(null);
  const nextId = useRef(0);
  const outputRef = useRef<HTMLDivElement>(null);

  // La sortie se lit toujours par le bas, comme un vrai terminal.
  useEffect(() => {
    const node = outputRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [lines]);

  const print = (fresh: readonly ShellLine[]): void => {
    setLines((previous) =>
      [...previous, ...fresh.map((line) => ({ ...line, id: nextId.current++ }))].slice(-SCROLLBACK),
    );
  };

  const apply = (action: ShellAction): void => {
    switch (action.kind) {
      case 'audio':
        if (action.what === 'toggle') actions.toggleAudio();
        else actions.stopPad();
        return;
      case 'pad':
        actions.hitPad(action.index);
        return;
      case 'preset':
        actions.preset(action.name);
        return;
      case 'toggle':
        if (action.what === 'hold') actions.toggleHold();
        else actions.toggleWobble();
        return;
      case 'skin':
        if (action.skin === 'random') actions.cycleSkin(0);
        else actions.setSkin(action.skin);
        return;
      case 'tank':
        if (action.what === 'feed') actions.feed();
        else if (action.what === 'praise') actions.praise();
        else actions.bubbles();
        return;
      case 'egg':
        actions.openEgg();
        return;
      case 'clear':
        setLines([]);
        return;
      case 'post':
        // Le livre d'or lit le brouillon partagé : il faut publier avant de
        // vider le champ.
        actions.postEntry();
    }
  };

  const submit = (): void => {
    const line = draft.trim();
    if (line === '') return;

    const result = runCommand(draft, { lang, seen, clock, playing, history });
    print([{ text: `$ ${line}`, tone: 'echo' }, ...result.lines]);
    setHistory((previous) => [...previous, line].slice(-SCROLLBACK));
    setRecalled(null);
    if (result.action) apply(result.action);
    // `post` a déjà vidé le champ en publiant ; les autres commandes le vident ici.
    if (result.action?.kind !== 'post') actions.setDraft('');
  };

  /** Rappelle une ligne de l'historique, du plus récent au plus ancien. */
  const recall = (direction: -1 | 1): void => {
    if (history.length === 0) return;
    const current = recalled ?? history.length;
    const index = Math.min(history.length, Math.max(0, current + direction));
    setRecalled(index >= history.length ? null : index);
    actions.setDraft(index >= history.length ? '' : (history[index] ?? ''));
  };

  return (
    <>
      {lines.length > 0 && (
        <div className={styles.output} ref={outputRef} role="log" aria-live="polite">
          {lines.map((line) => (
            <div
              key={line.id}
              className={cx(
                styles.outputLine,
                line.art === true && styles.outputArt,
                line.tone === 'dim' && styles.dim,
                line.tone === 'echo' && styles.echo,
              )}
            >
              {line.text}
            </div>
          ))}
        </div>
      )}

      <div className={styles.compose}>
        <span className={styles.prompt} aria-hidden="true">
          $
        </span>
        <input
          type="text"
          className={styles.input}
          value={draft}
          placeholder={t(S.guestbook.placeholder, lang)}
          aria-label={t(S.guestbook.placeholder, lang)}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => {
            setRecalled(null);
            actions.setDraft(event.currentTarget.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              submit();
              return;
            }
            if (event.key === 'Tab') {
              event.preventDefault();
              actions.setDraft(complete(draft));
              return;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              recall(-1);
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              recall(1);
            }
          }}
        />
        <button type="button" className={styles.button} onClick={submit}>
          {t(S.matrix.guestbook.send, lang)}
        </button>
      </div>

      <p className={cx(styles.dim, styles.shellHint)}>{t(S.matrix.term.hint, lang)}</p>
    </>
  );
}
